/*
Shared slug / URL helpers for the static doc system.

These are used in BOTH places that must agree on URLs:
- the build-time generator (bin/) which emits one /slug/index.html per doc
- the runtime <tosi-doc-system> component which resolves location.pathname -> doc
  and builds nav hrefs.

Because the generator and the component both operate on the same docs.json, every
function here is a pure, order-independent function of the docs array, so the two
sides always produce identical slugs.
*/

export interface DocLike {
  filename: string
  /** this doc is the site root (`/`) instead of README; see `homeDoc` */
  home?: boolean
}

const README = /^readme\.md$/i

/**
 * The doc served at the site root: the one doc marked `home`, or README when none is.
 * Marked by the build from the site's `home` option, or by a doc's own `{"home": true}`.
 * The build refuses a corpus with more than one, so a second mark is ignored here rather
 * than giving two docs the root.
 */
export function homeDoc(docs: DocLike[]): string | null {
  const marked = docs.filter((doc) => doc.home)
  return marked.length === 1 ? marked[0].filename : null
}

/**
 * filename without its final extension. The home doc maps to '' (the site root); README
 * does too unless another doc is home, and is then `readme`.
 */
function baseSlug(filename: string, home: string | null): string {
  if (filename === home) return ''
  if (README.test(filename)) return home == null ? '' : 'readme'
  return filename.replace(/\.[^.]+$/, '')
}

/**
 * Build a deterministic { filename -> slug } map for a set of docs.
 *
 * Collisions (e.g. `layout.ts` and `layout.css` both -> `layout`) are resolved by
 * falling back to the dotted filename with dots turned into dashes for EVERY member
 * of the colliding group. Computed from counts first, so the result does not depend
 * on array order.
 */
export function buildSlugMap(docs: DocLike[]): Record<string, string> {
  const home = homeDoc(docs)
  const counts: Record<string, number> = {}
  for (const doc of docs) {
    const base = baseSlug(doc.filename, home)
    counts[base] = (counts[base] || 0) + 1
  }
  const map: Record<string, string> = {}
  for (const doc of docs) {
    const base = baseSlug(doc.filename, home)
    // The root ('' base) is unique by construction; never disambiguate it.
    map[doc.filename] =
      base !== '' && counts[base] > 1 ? doc.filename.replace(/\./g, '-') : base
  }
  return map
}

/**
 * What each `[[target]]` in a corpus can reach: { slugify(target) -> slug }.
 *
 * A page is reached by its slug, and also by its title when no page has that slug and no
 * other page has the same title (GitHub #234). Authors write a page's TITLE —
 * `[[The feature registry]]` — while the page lives at its file name, `/registry/`; before
 * this, every such link went to `/the-feature-registry/` and 404'd without a word. A slug
 * always wins over a title, so no link that already worked changes target.
 */
export function wikilinkTargets(
  docs: Array<DocLike & { title?: string }>
): Map<string, string> {
  const slugs = buildSlugMap(docs)
  const targets = new Map<string, string>()
  for (const doc of docs) {
    const slug = slugs[doc.filename]
    if (slug !== '') targets.set(slugify(slug), slug)
  }
  const byTitle = new Map<string, string | null>()
  for (const doc of docs) {
    const key = doc.title ? slugify(doc.title) : ''
    if (!key || targets.has(key)) continue
    // Two pages with one title: neither is reachable by it, so the link is reported.
    byTitle.set(key, byTitle.has(key) ? null : slugs[doc.filename])
  }
  for (const [key, slug] of byTitle) if (slug !== null) targets.set(key, slug)
  return targets
}

/** site-root-relative path for a slug: '' -> '/', 'button' -> '/button/' */
export function pathForSlug(slug: string): string {
  return slug === '' ? '/' : `/${slug}/`
}

/**
 * Rewrite legacy `?<filename>` (and `/?<filename>`) doc links in an HTML string
 * to resolved hrefs. Used by the static generator so the pre-rendered pages have
 * clean links for no-JS readers, crawlers, and the brief window before the
 * doc-browser hydrates. `hrefFor` returns the target href for a known filename,
 * or null to leave the link untouched (unknown filename / a real query string).
 */
export function rewriteDocLinks(
  html: string,
  hrefFor: (filename: string) => string | null
): string {
  return html.replace(/href="\/?\?([^"&=]+)"/g, (match, query: string) => {
    const href = hrefFor(decodeURIComponent(query))
    return href === null ? match : `href="${href}"`
  })
}

/**
 * Map a legacy `?<filename>` query (the old doc-browser's query-param routing,
 * e.g. `?button.ts`, `?README.md`) to the new slug path (`/button/`, `/`).
 *
 * Returns the new path, or null when `search` isn't a bare-filename query or
 * doesn't match a known doc. Uses the slug map so README -> '/' and collision
 * disambiguation (foo.ts + foo.css -> /foo-ts/, /foo-css/) are handled.
 */
export function legacyQueryPath(
  search: string,
  slugMap: Record<string, string>
): string | null {
  const query = search.replace(/^\?/, '')
  // Legacy links are a single bare filename — anything with key=value pairs is
  // a real query string, not the old routing.
  if (!query || query.includes('=') || query.includes('&')) return null
  const filename = decodeURIComponent(query)
  const slug = slugMap[filename]
  if (slug === undefined) return null
  return pathForSlug(slug)
}

/** strip a pathname down to its slug: '/button/' -> 'button', '/' -> '' */
export function slugForPath(pathname: string): string {
  return pathname
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .replace(/\/index\.html$/i, '')
}

/**
 * Resolve a browser location.pathname to a doc filename, using a slug map.
 * Returns '' if nothing matches (caller falls back to the first/README doc).
 *
 * `aliases` ({ alias path -> slug }, the site config's `aliases`) are a doc's extra paths: a
 * page's own slug wins, so an alias never takes a path from a page.
 */
export function filenameForPath(
  pathname: string,
  slugMap: Record<string, string>,
  aliases: Record<string, string> = {}
): string {
  const slug = slugForPath(pathname)
  const find = (wanted: string): string => {
    for (const [filename, docSlug] of Object.entries(slugMap)) {
      if (docSlug === wanted) return filename
    }
    return ''
  }
  const own = find(slug)
  return own || (Object.hasOwn(aliases, slug) ? find(aliases[slug]) : '')
}

/**
 * Check a site's `aliases` against its slug map, and return the problems in words (empty when
 * there are none). An alias is one lowercase path segment with no dot, takes no page's own
 * path, and names a page.
 *
 * Lowercase because on a case-insensitive filesystem (macOS, Windows) writing `Guide/` replaces
 * `guide/`, and a crawler at `/guide/` then got another page. No dot because every file the
 * build writes at the root has one (`docs.json`, `sitemap.xml`, `llms.txt`).
 */
export function aliasProblems(
  aliases: Record<string, string>,
  slugMap: Record<string, string>
): string[] {
  const slugs = new Set(Object.values(slugMap))
  const folded = new Set([...slugs].map((s) => s.toLowerCase()))
  const problems: string[] = []
  for (const [alias, target] of Object.entries(aliases)) {
    if (!/^[a-z0-9][a-z0-9_-]*$/.test(alias)) {
      problems.push(
        `"${alias}" is not one lowercase path segment (a-z, 0-9, "_", "-")`
      )
    } else if (folded.has(alias)) {
      problems.push(`"${alias}" is already a page's own path`)
    }
    if (!slugs.has(target)) {
      problems.push(
        `"${alias}" names "${target}", which is no page's slug ('' is the root page)`
      )
    }
  }
  return problems
}

/** Turn a human name/title into a slug: 'Form Components' -> 'form-components'. */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Resolve a doc's `parent` value (a NAME or a slug) to the parent doc's
 * filename. Tries, in order: exact filename, exact slug (slug->slug no-op),
 * slugify(value) against doc slugs, then slugify(value) against slugify(title).
 * Returns '' if nothing matches (the build then auto-creates a section doc).
 */
export function resolveParent(
  parentValue: string,
  docs: Array<{ filename: string; title?: string }>,
  slugMap: Record<string, string>
): string {
  if (!parentValue) return ''
  for (const d of docs) if (d.filename === parentValue) return d.filename
  for (const d of docs)
    if (slugMap[d.filename] === parentValue) return d.filename
  const target = slugify(parentValue)
  for (const d of docs) if (slugMap[d.filename] === target) return d.filename
  for (const d of docs)
    if (d.title && slugify(d.title) === target) return d.filename
  return ''
}

/**
 * Prefix a root-relative path with the site's `basePath`.
 *
 * THE ONE COPY. There were two identical implementations (`generate-site.ts`, `epub.ts` —
 * the latter's comment said "mirrors generate-site's withBase") and a third consumer,
 * `make-llms-txt.ts`, that had none at all. So `baseUrl` meant the ORIGIN to the page
 * generator and ORIGIN-PLUS-PATH to llms.txt, and on a GitHub project page no configuration
 * satisfied both: matching the canonical URLs doubled the prefix in llms.txt, and matching
 * llms.txt dropped it from every canonical and sitemap entry (tosijs-ui#144).
 *
 * Nothing failed — `basePath` affects metadata only, so the site works and you find it by
 * reading the emitted `<head>`. The reporter's shipped to Pages before they noticed.
 *
 * `baseUrl` is the ORIGIN ONLY. Absolute URLs and an empty/`/` basePath pass through.
 */
export function withBase(basePath: string | undefined, p: string): string {
  if (!p || !basePath || basePath === '/' || /^(https?:)?\/\//.test(p)) return p
  return basePath.replace(/\/$/, '') + (p.startsWith('/') ? p : '/' + p)
}
